import React from "react";
import AsyncSelect from "react-select/async";
import { IsAllow } from "../../utils/allowHelper/AllowHelper";
import { useSelector } from "react-redux";
import { useGetCompanyQuery, useGetUserByIdQuery } from "store/api/apiSlice";
import { getCustomersReferredOptionsService } from "../../services/customers/services";

const ClientRelatedTo = ({
  relatedTo,
  onChange,
  allow,
  label,
  customerId,
  isMulti,
  readOnly,
  referrals = [],
}) => {
  const { user } = useSelector((state) => state.user);

  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    {
      skip: !userProfile?.user?.companyId,
    },
  );

  const isDark = window.matchMedia("(prefers-color-scheme: dark)").matches;

  const loadOptions = async (inputValue) => {
    const customers = await getClients(inputValue);
    return customers;
  };

  const withAlpha = (hex, alpha) =>
    `${hex}${Math.round(alpha * 255)
      .toString(16)
      .padStart(2, "0")}`;

  const styles = {
    control: (base) => ({
      ...base,
      backgroundColor: isDark ? "#374151" : "#ffffff",
      borderColor: isDark ? "#4b5563" : "#d1d5db",
      color: isDark ? "#ffffff" : "#000000",
      padding: "2px",
      boxShadow: "none",
      ":hover": {
        borderColor: isDark ? "#6b7280" : "#9ca3af",
      },
    }),

    menu: (base) => ({
      ...base,
      backgroundColor: isDark ? "#1f2937" : "#ffffff",
      border: `1px solid ${isDark ? "#4b5563" : "#d1d5db"}`,
      zIndex: 50,
    }),

    option: (base, { data, isFocused, isSelected }) => {
      const color = data.color;

      return {
        ...base,
        backgroundColor: isSelected
          ? color
          : isFocused
            ? withAlpha(color, isDark ? 0.2 : 0.12)
            : "transparent",
        color: isSelected ? "#ffffff" : isDark ? "#ffffff" : color,
        cursor: "pointer",
        ":active": {
          ...base[":active"],
          backgroundColor: isSelected
            ? color
            : withAlpha(color, isDark ? 0.35 : 0.2),
        },
      };
    },

    singleValue: (base) => ({
      ...base,
      color: isDark ? "#ffffff" : "#000000",
    }),

    input: (base) => ({
      ...base,
      color: isDark ? "#ffffff" : "#000000",
    }),

    placeholder: (base) => ({
      ...base,
      color: isDark ? "#9ca3af" : "#6b7280",
    }),
  };

  const getClients = async (search) => {
    let excludeIds = [];
    if (referrals.length > 0) {
      excludeIds = referrals.map((r) => r.referredId);
    }
    const data = await getCustomersReferredOptionsService({
      companyId: company?._id,
      page: 1,
      search,
      category: "all",
      communications: "all",
      pageSize: 5,
      customerId,
      excludeIds,
    });

    let customers = data?.customers ?? [];

    if (customerId) {
      customers = customers.filter((client) => client._id !== customerId);
    }
    return customers.map((client) => ({
      _id: client._id,
      accountNumber: client.accountNumber,
      firstName: client.firstName,
      lastName: client.lastName,
      companyId: client.companyId,
      value: client._id,
      label: `${client.accountNumber} - ${client.firstName} ${client.lastName}`,
      color: "#3b82f6",
    }));
  };

  const handleOnChange = (event) => {
    onChange(event);
  };

  if (!IsAllow(allow, "customer-01")) return null;

  if (userLoading || companyLoading) return null;

  return (
    <div>
      <label className="label">{label}</label>

      <AsyncSelect
        value={
          typeof relatedTo === "object"
            ? relatedTo
            : relatedTo?.map((rel) => ({
                value: rel._id,
                label: `${rel.accountNumber} - ${rel.firstName} ${rel.lastName}`,
                _id: rel._id,
                accountNumber: rel.accountNumber,
                firstName: rel.firstName,
                lastName: rel.lastName,
                companyId: rel.companyId,
                color: "#3b82f6",
              }))
        }
        cacheOptions
        loadOptions={loadOptions}
        onChange={handleOnChange}
        defaultOptions
        styles={styles}
        isMulti={isMulti}
        isDisabled={readOnly}
      />
    </div>
  );
};

export default ClientRelatedTo;
